import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  linkedSignal,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import {
  takeUntilDestroyed,
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import { AbilityServiceSignal } from '@casl/angular';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { captureException } from '@sentry/angular';
import { ActivitiesPipesModule } from '@wilson/activities/pipes';
import {
  CirculationGroupGateway,
  ShiftAttachmentsService,
} from '@wilson/api/gateway';
import { AuditTrailComponentsModule } from '@wilson/audit-trail/components';
import {
  AuditTrailDrawerState,
  SetAuditTrailDrawerCurrentShiftIdState,
  SetAuditTrailDrawerOpenState,
} from '@wilson/audit-trail/states';
import { FeatureFlagsModule } from '@wilson/feature-flags';
import {
  PublicationStatus,
  RoleAction,
  RolePermissionSubject,
  Shift,
} from '@wilson/interfaces';
import { LastUpdateStatusComponent } from '@wilson/non-domain-specific/drawer-common';
import { DrawerGroupingActionsService } from '@wilson/non-domain-specific/groupings';
import {
  CustomNzDrawerService,
  DrawerSettingsManagerDirective,
  NzDrawerContentComponent,
  NzDrawerHeaderComponent,
  NzDrawerId,
} from '@wilson/non-domain-specific/ng-zorro/nz-drawer/core';
import {
  TimelinePresenceService,
  ViewersAvatarsComponent,
} from '@wilson/non-domain-specific/realtime-collaboration/live-viewers';
import { MoreShiftActionsDropdownComponent } from '@wilson/non-domain-specific/shift-common-actions';
import {
  AcceptShiftButtonComponent,
  CanAllShiftBeModifiedPipe,
  CollapsibleShiftQuickCheckComponent,
  RevertShiftButtonComponent,
  SendShiftBackToAssigneeButtonComponent,
  ShiftFormPublishButtonComponent,
  ShiftQuickCheckService,
  ShiftStatusComponent,
} from '@wilson/non-domain-specific/shift-helpers/core';
import {
  ShiftDrawerCloseHelperService,
  ShiftMultiSelectHelperService,
} from '@wilson/shift-timeline/assignments-gantt-chart';
import { SetDemarcatorRange } from '@wilson/shift-timeline/state';
import { ShiftsService } from '@wilson/shifts';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzMessageRef, NzMessageService } from 'ng-zorro-antd/message';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import {
  catchError,
  distinctUntilChanged,
  firstValueFrom,
  of,
  switchMap,
} from 'rxjs';
import { ShiftFormEditButtonComponent } from '../shift-form-edit-button/shift-form-edit-button.component';
import { ShiftFormViewButtonComponent } from '../shift-form-view-button/shift-form-view-button.component';
import { InformationSectionComponent } from './information-section/information-section.component';
import { ReportedSectionComponent } from './reported-section/reported-section.component';
import { SimplifiedShiftDrawerFormService } from './simplified-shift-drawer-form.service';
import { SimplifiedShiftDrawerSaveService } from './simplified-shift-drawer-save.service';
import { SimplifiedShiftActivityOperationsService } from './services/simplified-shift-activity-operations.service';
import {
  SimplifiedShiftAssigneeSaveService,
  SimplifiedShiftDetailsSaveService,
  SimplifiedShiftReloadAndTimelineSyncService,
} from './services/save';
import { SimplifiedDrawerShift } from './types';

const SIMPLIFIED_SHIFT_DRAWER_RELATIONS = [
  'user',
  'user.contract',
  'user.contract.activityCategoryContracts',
  'labels',
  'payrollInformation',
  'activities',
  'activities.activityCategory',
  'activities.endLocation',
  'activities.startLocation',
  'activities.reportedEndLocation',
  'activities.reportedStartLocation',
  'activities.activityReports',
  'activities.service',
  'activities.agreement',
  'activities.agreement.client',
];

@Component({
  selector: 'wilson-simplified-shift-drawer',
  templateUrl: './simplified-shift-drawer.component.html',
  styleUrls: ['./simplified-shift-drawer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    SimplifiedShiftDrawerFormService,
    SimplifiedShiftDrawerSaveService,
    SimplifiedShiftActivityOperationsService,
    SimplifiedShiftReloadAndTimelineSyncService,
    SimplifiedShiftDetailsSaveService,
    SimplifiedShiftAssigneeSaveService,
    ShiftQuickCheckService,
    DrawerGroupingActionsService,
  ],
  imports: [
    AuditTrailComponentsModule,
    CommonModule,
    NzDrawerModule,
    DrawerSettingsManagerDirective,
    NzDrawerContentComponent,
    NzDrawerHeaderComponent,
    LastUpdateStatusComponent,
    FeatureFlagsModule,
    NzSkeletonModule,
    NzTooltipModule,
    TranslateModule,
    ShiftStatusComponent,
    ViewersAvatarsComponent,
    MoreShiftActionsDropdownComponent,
    ShiftFormPublishButtonComponent,
    RevertShiftButtonComponent,
    SendShiftBackToAssigneeButtonComponent,
    AcceptShiftButtonComponent,
    ShiftFormViewButtonComponent,
    ShiftFormEditButtonComponent,
    CanAllShiftBeModifiedPipe,
    ActivitiesPipesModule,
    CollapsibleShiftQuickCheckComponent,
    InformationSectionComponent,
    ReportedSectionComponent,
  ],
})
export class SimplifiedShiftDrawerComponent implements OnDestroy {
  private readonly shiftsService = inject(ShiftsService);
  private readonly message = inject(NzMessageService);
  private readonly translate = inject(TranslateService);
  private readonly formService = inject(SimplifiedShiftDrawerFormService);
  private readonly saveService = inject(SimplifiedShiftDrawerSaveService);
  private readonly store = inject(Store);
  private readonly customNzDrawerService = inject(CustomNzDrawerService);
  private readonly shiftMultiSelectHelperService = inject(
    ShiftMultiSelectHelperService,
  );
  private readonly timelinePresenceService = inject(TimelinePresenceService);
  private readonly circulationGroupGateway = inject(CirculationGroupGateway);
  private readonly shiftAttachmentsService = inject(ShiftAttachmentsService);
  protected readonly shiftQuickCheckService = inject(ShiftQuickCheckService);
  private readonly drawerGroupingActions = inject(DrawerGroupingActionsService);
  protected readonly shiftDrawerCloseHelperService = inject(
    ShiftDrawerCloseHelperService,
  );
  protected readonly can = inject(AbilityServiceSignal).can;

  readonly selectedShiftId = input<string | null>(null);
  readonly closeDrawer = output<void>();
  readonly deletionConfirmed = output<Shift & { id: string }>();

  protected drawerId = NzDrawerId.SimplifiedShiftDrawer;
  protected readonly RoleAction = RoleAction;
  protected readonly RolePermissionSubject = RolePermissionSubject;
  protected readonly PublicationStatus = PublicationStatus;

  readonly updatedAtSignal = signal<string>(new Date().toISOString());
  readonly isSuccessfullySavedSignal = signal<boolean>(false);

  private readonly selectedShiftId$ = toObservable(this.selectedShiftId);

  readonly isShiftUpdateOperationOngoingSignal = signal<boolean>(false);
  readonly isFormInvalid = signal(false);
  private initializedShiftIdForCurrentOpen: string | null = null;
  private clickOutsideOfIgnoredTargetsHandlerRef!: (event: Event) => void;
  private savingMessageRef: NzMessageRef | null = null;

  private readonly fetchedShiftSignal = toSignal(
    this.selectedShiftId$.pipe(
      distinctUntilChanged(),
      switchMap((shiftId) => {
        if (!shiftId) {
          return of(null);
        }
        return this.shiftsService
          .get<SimplifiedDrawerShift>(shiftId, {
            relations: SIMPLIFIED_SHIFT_DRAWER_RELATIONS,
          })
          .pipe(
            catchError((error) => {
              captureException(error);
              this.message.error(
                this.translate.instant('general.something_went_wrong'),
              );
              return of(null);
            }),
          );
      }),
    ),
    { initialValue: null },
  );

  protected readonly shift = linkedSignal<SimplifiedDrawerShift | null>(
    () => this.fetchedShiftSignal() ?? null,
  );

  readonly hasServiceActivitySignal = computed(() => {
    const selectedShift = this.shift();
    if (!selectedShift) {
      return false;
    } else {
      return selectedShift.activities.some((a) => a.serviceId);
    }
  });

  readonly shiftViewers = toSignal(
    this.shiftMultiSelectHelperService.singleSelectedShiftIdWithPosition$.pipe(
      switchMap((shiftIdWithPosition) => {
        const shiftId = shiftIdWithPosition?.id;
        if (!shiftId) {
          return of([]);
        }
        return this.timelinePresenceService.getShiftViewers(shiftId);
      }),
    ),
    { initialValue: [] },
  );

  constructor() {
    this.drawerGroupingActions.configure({
      onSuccess: async () => {
        await this.reloadShiftAndUpdateTimeline();
      },
    });

    this.saveService.initialize({
      formService: this.formService,
      getCurrentShift: () => this.shift(),
      onSaveStateChange: ({
        isSaving,
        isSuccessfullySaved,
        showSavingIndicator = true,
      }) => {
        if (showSavingIndicator) {
          this.updateSavingMessage(isSaving);
        } else if (!isSaving) {
          this.clearSavingMessage();
        }
        this.isShiftUpdateOperationOngoingSignal.set(isSaving);
        this.isSuccessfullySavedSignal.set(isSuccessfullySaved);
        if (isSuccessfullySaved) {
          this.updatedAtSignal.set(new Date().toISOString());
          this.isFormInvalid.set(this.formService.form.invalid);
          this.syncCloseHelperPristineState();
          // ngx-editor may dirty matching controls after writeValue; re-sync.
          queueMicrotask(() => {
            this.formService.clearSpuriousDirtyMatchingLastInit();
            this.syncCloseHelperPristineState();
          });
        }
      },
      onShiftUpdated: (reloadedShift) => {
        this.shift.set(reloadedShift);
      },
    });

    this.handleDrawerOpened();

    effect(() => {
      const selectedShift = this.shift();

      if (
        selectedShift &&
        selectedShift.id !== this.initializedShiftIdForCurrentOpen
      ) {
        void this.initializeDrawerForShift(selectedShift);
      }
    });

    this.formService.form.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.formService.clearSpuriousDirtyMatchingLastInit();
        this.isFormInvalid.set(this.formService.form.invalid);
        this.syncCloseHelperPristineState();
      });
  }

  protected toggleChangeHistoryDrawer(): void {
    if (this.store.selectSnapshot(AuditTrailDrawerState.isOpen)) {
      this.store.dispatch(new SetAuditTrailDrawerOpenState(false));
      return;
    }

    const selectedShift = this.shift();
    if (!selectedShift) {
      return;
    }

    this.store.dispatch(
      new SetAuditTrailDrawerCurrentShiftIdState(selectedShift.id),
    );
    this.store.dispatch(new SetAuditTrailDrawerOpenState(true));
  }

  protected async reloadShiftAndUpdateTimeline(options?: {
    updatePublicationStatus?: boolean;
  }): Promise<void> {
    const selectedShift = this.shift();
    if (!selectedShift) {
      return;
    }

    this.isShiftUpdateOperationOngoingSignal.set(true);
    this.isSuccessfullySavedSignal.set(false);

    try {
      const reloadedShift = await this.saveService.reloadShiftAndUpdateTimeline(
        selectedShift.id,
        options,
      );

      if (reloadedShift) {
        this.shift.set(reloadedShift);
        this.formService.initialize(reloadedShift, {
          preserveTierTwoPromotion: true,
        });
        this.saveService.notifyFormInitialized(reloadedShift);
        this.isFormInvalid.set(this.formService.form.invalid);
        this.shiftDrawerCloseHelperService.isFormPristine = true;
        this.isSuccessfullySavedSignal.set(true);
        this.updatedAtSignal.set(new Date().toISOString());
      }
    } catch (error) {
      captureException(error);
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
      this.isSuccessfullySavedSignal.set(false);
    } finally {
      this.isShiftUpdateOperationOngoingSignal.set(false);
    }
  }

  protected async updateShiftNecessaryStay(value: boolean): Promise<void> {
    const selectedShift = this.shift();
    if (!selectedShift) {
      return;
    }

    this.isShiftUpdateOperationOngoingSignal.set(true);
    this.isSuccessfullySavedSignal.set(false);

    try {
      await firstValueFrom(
        this.shiftsService.updateShift({
          id: selectedShift.id,
          ignoreStayNecessary: value,
        }),
      );
      await this.reloadShiftAndUpdateTimeline();
    } catch (error) {
      captureException(error);
      this.message.error(
        this.translate.instant('general.something_went_wrong'),
      );
      this.isSuccessfullySavedSignal.set(false);
    } finally {
      this.isShiftUpdateOperationOngoingSignal.set(false);
    }
  }

  private async initializeDrawerForShift(
    selectedShift: SimplifiedDrawerShift,
  ): Promise<void> {
    this.formService.initialize(selectedShift);
    this.saveService.notifyFormInitialized(selectedShift);
    await Promise.all([
      this.promoteTierTwoSectionsFromShiftData(selectedShift.id),
      this.shiftQuickCheckService.validateShiftForQuickCheck(
        selectedShift.id,
        selectedShift.userId ?? null,
      ),
    ]);
    this.formService.lockTierTwoPromotion();
    this.initializedShiftIdForCurrentOpen = selectedShift.id;
    this.isFormInvalid.set(this.formService.form.invalid);
    this.shiftDrawerCloseHelperService.isFormPristine = true;
  }

  private async promoteTierTwoSectionsFromShiftData(
    shiftId: string,
  ): Promise<void> {
    try {
      const [attachments, groupingItems] = await Promise.all([
        firstValueFrom(
          this.shiftAttachmentsService
            .getShiftAttachments(shiftId)
            .pipe(catchError(() => of([]))),
        ),
        firstValueFrom(
          this.circulationGroupGateway
            .getItems(shiftId)
            .pipe(catchError(() => of([]))),
        ),
      ]);

      if (attachments.length > 0) {
        this.formService.promoteTierTwoSection('attachments');
      }

      if (groupingItems.length > 1) {
        this.formService.promoteTierTwoSection('grouping');
      }
    } catch (error) {
      captureException(error);
    }
  }

  ngOnDestroy(): void {
    this.handleDrawerClosed();
    this.clearSavingMessage();
  }

  private clearSavingMessage(): void {
    this.message.remove(this.savingMessageRef?.messageId);
    this.savingMessageRef = null;
  }

  private syncCloseHelperPristineState(): void {
    this.shiftDrawerCloseHelperService.isFormPristine =
      this.formService.form.pristine;
  }

  private updateSavingMessage(isSaving: boolean): void {
    if (isSaving) {
      if (!this.savingMessageRef) {
        this.savingMessageRef = this.message.loading(
          this.translate.instant('general.saving'),
        );
      }
      return;
    }

    this.clearSavingMessage();
  }

  private handleDrawerOpened(): void {
    this.customNzDrawerService.showMask();
    this.clickOutsideOfIgnoredTargetsHandlerRef =
      this.customNzDrawerService.registerClickOutsideOfIgnoredTargetsHandler(
        () => {
          this.shiftDrawerCloseHelperService.checkPristineAndCloseDrawer({
            onOkCallback: this.closeDrawerConfirmCallback.bind(this),
          });
        },
      );
  }

  private handleDrawerClosed(): void {
    this.customNzDrawerService.removeClickHandler(
      this.clickOutsideOfIgnoredTargetsHandlerRef,
    );
    this.customNzDrawerService.hideMask();
    this.shiftDrawerCloseHelperService.isFormPristine = true;
    this.formService.resetTierTwoPromotion();
    this.initializedShiftIdForCurrentOpen = null;
  }

  private closeDrawerConfirmCallback(): void {
    this.shiftMultiSelectHelperService.clearSelection();
    this.store.dispatch([new SetDemarcatorRange([])]);
  }
}
